// Constantes y utilidades compartidas por las pestañas del panel de administración.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CATEGORY_TREE } from '../../data/shopProducts';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;
export const CATEGORIES = ['PARCHE', 'EVENTO', 'PVP', 'AVISO'];

export const CATEGORY_COLORS = {
  PARCHE: { bg: 'rgba(0,120,255,0.18)',  border: 'rgba(0,120,255,0.45)',  text: '#69CCF0' },
  EVENTO: { bg: 'rgba(255,140,0,0.15)',  border: 'rgba(255,140,0,0.45)',  text: '#FFAA44' },
  PVP:    { bg: 'rgba(200,30,30,0.15)',  border: 'rgba(200,30,30,0.45)',  text: '#FF6666' },
  AVISO:  { bg: 'rgba(160,50,255,0.15)', border: 'rgba(160,50,255,0.45)', text: '#B080FF' },
};

export const apiErr = (err, fallback = 'Error desconocido') => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(' · ');
  return fallback;
};

// ─────────────────────────────────────────────────────────────────────────────
// Datos auxiliares para el editor de tienda
// ─────────────────────────────────────────────────────────────────────────────
export const SHOP_QUALITIES = ['legendary', 'epic', 'rare', 'uncommon'];
export const SHOP_ACTION_TYPES = [
  'send_items', 'levelup', 'dual_profession', 'secondary_profession',
  'change_name', 'customize', 'change_race', 'change_faction', 'voucher',
];

// Servicios que admiten bonos (VOUCHER_PRODUCTS en backend/shop_router.py).
export const VOUCHER_LABELS = {
  pase_nivel_80:      'Subida a 80',
  maestria_profesion: 'Maestría en 2 profesiones',
  paquete_vuelo:      'Vuelo Ártico',
  cambio_faccion:     'Cambio de Facción',
  caja_fuerte_mercenario: 'Alijo del Mercenario',
};
export const voucherText = (v) => Object.entries(v || {}).filter(([, n]) => n > 0)
  .map(([k, n]) => `${n} × ${VOUCHER_LABELS[k] ?? k}`).join(' · ');

// Construye lista plana: [{ cat, sub, subSub? }] para los selectores
export function buildCategoryOptions() {
  const opts = [];
  Object.entries(CATEGORY_TREE).forEach(([catKey, catVal]) => {
    catVal.subs.forEach((sub) => {
      if (sub.subSubs) {
        sub.subSubs.forEach((ss) => {
          opts.push({ label: `${catVal.label} › ${sub.label} › ${ss.label}`, category: catKey, subcategory: sub.key, subsubcategory: ss.key });
        });
      } else {
        opts.push({ label: `${catVal.label} › ${sub.label}`, category: catKey, subcategory: sub.key, subsubcategory: '' });
      }
    });
  });
  return opts;
}
export const CATEGORY_OPTIONS = buildCategoryOptions();

export const EMPTY_PRODUCT_FORM = {
  id: '', name: '', subtitle: '', icon: 'inv_misc_questionmark',
  quality: 'epic', badge: '', price_crystals: 500,
  category: 'comandante', subcategory: 'servicios', subsubcategory: '',
  action_type: 'send_items', features_text: '', wow_item_ids: '', hidden: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// Stats helpers
// ─────────────────────────────────────────────────────────────────────────────
export const STAT_CLASS_COLORS = {
  1:'#C69B3A',2:'#F58CBA',3:'#ABD473',4:'#FFF569',5:'#FFFFFF',
  6:'#C41F3B',7:'#0070DE',8:'#69CCF0',9:'#9482C9',11:'#FF7D0A',
};
export const STAT_CLASS_NAMES = {
  1:'Guerrero',2:'Paladín',3:'Cazador',4:'Pícaro',5:'Sacerdote',
  6:'Caballero de la Muerte',7:'Chamán',8:'Mago',9:'Brujo',11:'Druida',
};
export const STAT_RACE_NAMES = {
  1:'Humano',2:'Orco',3:'Enano',4:'Elfo de la Noche',5:'No-Muerto',
  6:'Tauren',7:'Gnomo',8:'Troll',10:'Elfo de Sangre',11:'Draenei',
};
export function formatPlayed(secs) {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─────────────────────────────────────────────────────────────────────────────
// GeoCell — fetches geolocation lazily for a given IP address
// ─────────────────────────────────────────────────────────────────────────────
export function GeoCell({ ip }) {
  const [loc, setLoc] = useState(null);
  useEffect(() => {
    if (!ip || ip === '0.0.0.0') { setLoc('—'); return; }
    axios.get(`${BACKEND_URL}/api/admin/geoip/${encodeURIComponent(ip)}`, { withCredentials: true })
      .then(({ data }) => setLoc(data.location))
      .catch(() => setLoc('—'));
  }, [ip]);
  if (!ip || ip === '0.0.0.0') return <span style={{ color: 'rgba(224,247,255,0.2)' }}>—</span>;
  if (loc === null) return <span style={{ color: 'rgba(224,247,255,0.3)', fontSize: 11 }}>...</span>;
  return <span style={{ color: 'rgba(224,247,255,0.55)', fontSize: 11 }}>{loc}</span>;
}

export function KpiCard({ icon, label, value, subtitle }) {
  return (
    <div style={{ ...cardStyle, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(224,247,255,0.55)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
        <span style={{ color: '#22d3ee' }}>{icon}</span>
        {label}
      </div>
      <div style={{ marginTop: 8, fontSize: 26, fontWeight: 700, color: '#e0f7ff' }}>{value}</div>
      {subtitle && <div style={{ fontSize: 11, color: 'rgba(224,247,255,0.45)', marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

export const cardStyle = {
  padding:      18,
  borderRadius: 12,
  border:       '1px solid rgba(34, 211, 238, 0.18)',
  background:   'rgba(11, 17, 32, 0.55)',
};

export const sectionTitleStyle = {
  display:      'flex',
  alignItems:   'center',
  gap:          8,
  margin:       0,
  fontSize:     14,
  fontWeight:   700,
  color:        '#e0f7ff',
  letterSpacing: 0.4,
};

export const thStyle = { padding: '8px 10px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' };
export const tdStyle = { padding: '8px 10px', color: 'rgba(224,247,255,0.75)', verticalAlign: 'top' };

// Motivos del ledger de cristales (crystal_ledger.reason)
export const REASON_LABELS = {
  opening_balance: 'Saldo de apertura', stripe_topup: 'Recarga (Stripe)', vote: 'Voto', survey: 'Encuesta',
  referral_referrer: 'Referido (padrino)', referral_referred: 'Referido (invitado)', shop_purchase: 'Compra en tienda',
  shop_refund: 'Devolución de compra', admin_grant: 'Concedido por admin', admin_set: 'Saldo fijado por admin',
  admin_adjust: 'Ajuste manual',
};
// Estado de entrega de una compra (admin_overview.delivery_status en el backend)
export const DELIVERY = {
  delivered:  { label: '✓ Entregado',        color: '#34d399' },
  mailed:     { label: '✉ En el buzón',      color: '#22d3ee', hint: 'Enviado por correo del juego; no sabemos si ya lo recogió.' },
  next_login: { label: '↻ Al entrar',        color: '#93c5fd', hint: 'Se aplica la próxima vez que entre con ese personaje.' },
  pending:    { label: '⏳ Pendiente',        color: '#fbbf24', hint: 'Encargo del worldserver aún sin aplicar: se aplica al entrar al juego.' },
  failed:     { label: '✗ Falló · revisar',  color: '#f87171', hint: 'El worldserver rechazó el encargo y la compra ya estaba cobrada: hay que devolverla a mano.' },
  cancelled:  { label: '⊘ Cancelado',        color: 'rgba(224,247,255,0.5)' },
  vouchers:   { label: '🎟 Bonos añadidos',   color: '#fcd34d' },
  unknown:    { label: '· Procesada',        color: 'rgba(224,247,255,0.5)' },
};
